import { dictionaryCRUD } from "../../actions/dictionary";
import { buildingCRUD } from "../../actions/building";
import { boundaryCRUD } from "../../actions/boundary";
import { selector } from "../../api";
import { BuildingLocationCascade } from "./BuildingLocationCascade";
import { GpsButton } from "./GpsButton";
import Select from "react-select";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

const buildingSchema = Yup.object().shape({
  dictionary_building_category: Yup.string().required("Building type is required"),
  boundary_id: Yup.string().required("Shehia is required"),
  zone_id: Yup.string().required("Zone is required"),
  street_id: Yup.string(), // Street/Road is optional
});

const initialValues = {
  name: "",
  postcode: "",
  house_no: "",
  elecricity_meter_no: "",
  water_meter_no: "",
  number_of_units: 1,
  number_of_floors: 1,
  is_condominium: false,
  dictionary_building_category: "",
  dictionary_building_type: "",
  boundary_id: "",
  zone_id: "",
  street_id: "",
  centroid_lat: "",
  centroid_lng: "",
  centroid_coords: "",
  dictionary_water_source: "",
  distance_to_health_center: "",
  distance_to_market: "",
  distance_to_education_center: "",
  distance_to_water_source: "",
};

export const AddBuilding = (props) => {
  const { setIndex, index } = props;

  const user = selector.User();
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [waterSources, setWaterSources] = useState([]);
  const [selectedWaterSource, setSelectedWaterSource] = useState(null);
  // Sub-categories (e.g. School -> Primary/Secondary), loaded when a type is chosen.
  const [subTypes, setSubTypes] = useState([]);
  const [selectedSubType, setSelectedSubType] = useState(null);

  const numOrNull = (v) => (v !== "" && v != null ? parseFloat(v) : null);

  // Load sub-categories whose parent is the chosen building type.
  const loadSubTypes = (categoryId) => {
    if (!categoryId) { setSubTypes([]); return; }
    dictionaryCRUD.loadDictionaryByParent(categoryId)
      .then((resp) => setSubTypes(resp.data.results || resp.data || []))
      .catch(() => setSubTypes([]));
  };

  const formik = useFormik({
    initialValues,
    validationSchema: buildingSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      const {
        name,
        postcode,
        house_no,
        elecricity_meter_no,
        water_meter_no,
        number_of_floors,
        number_of_units,
        is_condominium,
        dictionary_building_category,
        dictionary_building_type,
        boundary_id,
        zone_id,
        street_id,
        centroid_lat,
        centroid_lng,
        dictionary_water_source,
        distance_to_health_center,
        distance_to_market,
        distance_to_education_center,
        distance_to_water_source,
      } = values;
      const recorded_by = user.id;
      const data = {
        name,
        postcode,
        house_no,
        elecricity_meter_no,
        water_meter_no,
        number_of_floors,
        number_of_units,
        is_condominium,
        dictionary_building_category,
        dictionary_building_type: dictionary_building_type || null,
        boundary_id,
        zone_id,
        street_id,
        recorded_by,
        // Optional coordinates -> building_centroid (omit if blank).
        centroid_lat: centroid_lat !== "" ? parseFloat(centroid_lat) : null,
        centroid_lng: centroid_lng !== "" ? parseFloat(centroid_lng) : null,
        dictionary_water_source: dictionary_water_source || null,
        distance_to_health_center: numOrNull(distance_to_health_center),
        distance_to_market: numOrNull(distance_to_market),
        distance_to_education_center: numOrNull(distance_to_education_center),
        distance_to_water_source: numOrNull(distance_to_water_source),
      };
      setLoading(true);
      setTimeout(() => {
        buildingCRUD
          .addBuilding(data)
          .then(() => {
            toast.success("Building has been registered.", { autoClose: 7000 });
            setIndex(index + 1);
            resetForm();
            setSelectedType(null);
            setSelectedWaterSource(null);
          })
          .catch((error) => {
            toast.error(error.message || "Failed to add building", { autoClose: 7000 });
          })
          .finally(() => {
            setSubmitting(false);
            setLoading(false);
          });
      }, 500);
    },
  });

  useEffect(() => {
    // Load building types (dictionary id 9). Only TOP-LEVEL types here
    // (no dictionary_item_parent) — sub-categories load separately by parent.
    dictionaryCRUD.loadDictionaryData(50, 0, "", 9).then((resp) => {
      setType((resp.data.results || []).filter((t) => !t.dictionary_item_parent));
    });
    dictionaryCRUD.loadDictionaryData(50, 0, "", 25).then((resp) => {
      setWaterSources(resp.data.results);
    }).catch(() => {});
  }, []);

  return (
    <>
      <div
        className="modal fade bs-example-modal-lg"
        tabIndex="-1"
        role="dialog"
        aria-labelledby="myLargeModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header bg-soft-primary">
              <h5 className="modal-title d-flex align-items-center" id="myLargeModalLabel">
                <i className="ri-home-4-line fs-18 me-2 text-primary"></i>
                Register New Building
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <form onSubmit={formik.handleSubmit}>
              <div className="modal-body" style={{ maxHeight: "calc(100vh - 220px)", overflowY: "auto" }}>

                {/* ── Section 1: Location ───────────────────────────── */}
                <div className="mb-4">
                  <h6 className="text-primary fw-semibold mb-1">
                    <i className="ri-map-pin-2-line align-bottom me-1"></i>
                    Location
                  </h6>
                  <p className="text-muted small mb-3">Pin the building down to its Shehia, Zone and Street.</p>
                  <BuildingLocationCascade
                    onChange={({ boundaryId, zoneId, streetId }) => {
                      formik.setFieldValue("boundary_id", boundaryId || "");
                      formik.setFieldValue("zone_id", zoneId || "");
                      formik.setFieldValue("street_id", streetId || "");
                    }}
                  />
                  {((formik.touched.boundary_id && formik.errors.boundary_id) ||
                    (formik.touched.zone_id && formik.errors.zone_id) ||
                    (formik.touched.street_id && formik.errors.street_id)) && (
                    <div className="text-danger small mt-1" role="alert">
                      <i className="ri-error-warning-line align-bottom me-1"></i>
                      {formik.errors.boundary_id || formik.errors.zone_id || formik.errors.street_id}
                    </div>
                  )}
                  <div className="row g-3 mt-1">
                    <div className="col-md-8">
                      <label className="form-label">Coordinates <span className="text-muted small">(Latitude, Longitude — optional)</span></label>
                      <input
                        type="text"
                        className="form-control"
                        value={formik.values.centroid_coords}
                        onChange={(e) => {
                          const raw = e.target.value;
                          formik.setFieldValue("centroid_coords", raw);
                          const parts = raw.split(/[,\s]+/).map((p) => p.trim()).filter(Boolean);
                          if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                            formik.setFieldValue("centroid_lat", parts[0]);
                            formik.setFieldValue("centroid_lng", parts[1]);
                          } else {
                            formik.setFieldValue("centroid_lat", "");
                            formik.setFieldValue("centroid_lng", "");
                          }
                        }}
                        placeholder="e.g. -6.16, 39.20"
                      />
                    </div>
                    <div className="col-md-4 d-flex align-items-end">
                      <GpsButton
                        onLocate={(lat, lng) => {
                          formik.setFieldValue("centroid_lat", lat);
                          formik.setFieldValue("centroid_lng", lng);
                          formik.setFieldValue("centroid_coords", `${lat}, ${lng}`);
                        }}
                      />
                    </div>
                  </div>
                  <small className="text-muted d-block mt-1">
                    <i className="ri-information-line align-bottom me-1"></i>
                    Sets the building's map point. You can draw the full outline on the map later.
                  </small>
                </div>

                {/* ── Section 2: Building details ───────────────────── */}
                <div className="mb-4">
                  <h6 className="text-primary fw-semibold mb-3">
                    <i className="ri-building-2-line align-bottom me-1"></i>
                    Building Details
                  </h6>
                  {/* Row 1: Type + Category */}
                  <div className="row g-3 mb-1">
                    <div className="col-md-6">
                      <label className="form-label">
                        Building Type <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={type?.map((dt) => ({
                          value: dt.id,
                          label: dt.dictionary_item_name_en,
                        }))}
                        value={selectedType}
                        placeholder="Select building type"
                        onChange={(e) => {
                          formik.setFieldValue("dictionary_building_category", e ? e.value : "");
                          setSelectedType(e);
                          // Reset + reload sub-categories for the new type.
                          formik.setFieldValue("dictionary_building_type", "");
                          setSelectedSubType(null);
                          loadSubTypes(e ? e.value : null);
                        }}
                        isSearchable
                        isClearable
                        name="dictionary_building_category"
                      />
                      {formik.touched.dictionary_building_category &&
                        formik.errors.dictionary_building_category && (
                          <div className="text-danger small mt-1" role="alert">
                            {formik.errors.dictionary_building_category}
                          </div>
                        )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Category</label>
                      <Select
                        options={subTypes.map((dt) => ({
                          value: dt.id,
                          label: dt.dictionary_item_name_en,
                        }))}
                        value={selectedSubType}
                        placeholder={subTypes.length ? "Select category" : "No categories for this type"}
                        onChange={(e) => {
                          formik.setFieldValue("dictionary_building_type", e ? e.value : "");
                          setSelectedSubType(e);
                        }}
                        isSearchable
                        isClearable
                        isDisabled={!subTypes.length}
                        name="dictionary_building_type"
                      />
                    </div>
                  </div>

                  {/* Row 2: Name + Shehia Number */}
                  <div className="row g-3 mb-1">
                    <div className="col-md-6">
                      <label className="form-label">Building Name</label>
                      <input
                        type="text"
                        className="form-control"
                        {...formik.getFieldProps("name")}
                        placeholder="Building name (optional)"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Shehia Number</label>
                      <input
                        type="text"
                        className="form-control"
                        {...formik.getFieldProps("house_no")}
                        placeholder="e.g. SH-338"
                      />
                    </div>
                  </div>

                  {/* Row 3: Postcode + Units + Floors */}
                  <div className="row g-3 mb-2">
                    <div className="col-md-4">
                      <label className="form-label">Postcode</label>
                      <input
                        type="text"
                        className="form-control"
                        {...formik.getFieldProps("postcode")}
                        placeholder="Postcode"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Units</label>
                      <input
                        type="number"
                        className="form-control"
                        {...formik.getFieldProps("number_of_units")}
                        placeholder="1"
                        min="1"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Floors</label>
                      <input
                        type="number"
                        className="form-control"
                        {...formik.getFieldProps("number_of_floors")}
                        placeholder="1"
                        min="1"
                      />
                    </div>
                  </div>

                  {/* Row 4: Condominium */}
                  <div className="row g-3">
                    <div className="col-md-12">
                      <div className="form-check form-switch form-switch-lg form-switch-success mt-1">
                        <input
                          className="form-check-input code-switcher"
                          checked={formik.values.is_condominium}
                          {...formik.getFieldProps("is_condominium")}
                          type="checkbox"
                          id="is_condominium_add"
                        />
                        <label className="form-check-label" htmlFor="is_condominium_add">
                          Condominium?
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Section 3: Utilities & Services ───────────────── */}
                <div className="mb-2">
                  <h6 className="text-primary fw-semibold mb-3">
                    <i className="ri-flashlight-line align-bottom me-1"></i>
                    Utilities &amp; Services
                  </h6>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Electricity Meter No.</label>
                      <input
                        type="text"
                        className="form-control"
                        {...formik.getFieldProps("elecricity_meter_no")}
                        placeholder="Electricity meter number"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Water Meter No.</label>
                      <input
                        type="text"
                        className="form-control"
                        {...formik.getFieldProps("water_meter_no")}
                        placeholder="Water meter number"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Source of Water</label>
                      <Select
                        options={waterSources?.map((dt) => ({ value: dt.id, label: dt.dictionary_item_name_en }))}
                        value={selectedWaterSource}
                        placeholder="Select water source"
                        onChange={(e) => {
                          formik.setFieldValue("dictionary_water_source", e ? e.value : "");
                          setSelectedWaterSource(e);
                        }}
                        isSearchable
                        isClearable
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Distance to Health (km)</label>
                      <input type="number" step="any" className="form-control" {...formik.getFieldProps("distance_to_health_center")} placeholder="0" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Distance to Market (km)</label>
                      <input type="number" step="any" className="form-control" {...formik.getFieldProps("distance_to_market")} placeholder="0" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Distance to Education (km)</label>
                      <input type="number" step="any" className="form-control" {...formik.getFieldProps("distance_to_education_center")} placeholder="0" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Distance to Water (km)</label>
                      <input type="number" step="any" className="form-control" {...formik.getFieldProps("distance_to_water_source")} placeholder="0" />
                    </div>
                  </div>
                </div>

              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light fw-medium"
                  data-bs-dismiss="modal"
                >
                  <i className="ri-close-line me-1 align-middle"></i> Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={formik.isSubmitting || !formik.isValid}
                >
                  {!loading && (
                    <span className="indicator-label">
                      <i className="ri-save-3-line me-1 align-middle"></i> Save Building
                    </span>
                  )}
                  {loading && (
                    <span className="indicator-progress" style={{ display: "block" }}>
                      Please wait...
                      <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
